import { Spacer } from "@hackthenorth/north";
import debounce from "lodash.debounce";
import { Button, Table, Text, TextInput } from "north.js";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";

import { QueryMode } from "src/api/types.generated";
import { Modal } from "src/shared/components";
import Autocomplete from "src/shared/components/Autocomplete";
import { useUserContext } from "src/shared/contexts";
import { deviceBreakpoints } from "src/theme/deviceBreakpoints";
import { useDetailedMentorShiftsQuery } from "src/views/mentor/shift-schedule/graphql/detailedMentorShifts.generated";
import { useMentorShiftsQuery } from "src/views/mentor/shifts/graphql/mentorShifts.generated";
import { useCreateShiftSignupMutation } from "src/views/volunteer/shared/components/VolunteerShiftModal/graphql/createShiftSignup.generated";
import { useDeleteVolunteerShiftSignupMutation } from "src/views/volunteer/shared/components/VolunteerShiftModal/graphql/deleteVolunteerShiftSignup.generated";
import {
  formatVolunteerShiftDay,
  getVolunteerShiftHourAndMinute,
} from "src/views/volunteer/shared/utils/date";
import "twin.macro";
import { useMentorsLazyQuery } from "src/views/mentor/shift-schedule/graphql/mentors.generated";
import { parseMaxCountRestriction } from "src/views/mentor/shift-schedule/utils";

type Props = {
  shiftId: number;
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  onDeleteShift?: () => void;
  onChange?: () => void;
};

const MentorShiftModal = ({ shiftId, open, setOpen, onChange }: Props) => {
  const [createShift] = useCreateShiftSignupMutation();
  const [deleteShiftSignup] = useDeleteVolunteerShiftSignupMutation();

  const navigate = useNavigate();

  const {
    data: mentorShiftData,
    error,
    loading,
  } = useMentorShiftsQuery({
    variables: { where: { id: { equals: shiftId } } },
  });

  const mentorShift = mentorShiftData?.mentorShifts[0];

  const { data: shiftSignupsData, refetch: refetchMentorSignups } =
    useDetailedMentorShiftsQuery({
      variables: { where: { id: { equals: shiftId } } },
    });

  const [getMentor, { data: mentorData }] = useMentorsLazyQuery();
  const [getMentorsList, { data: mentorsListData }] = useMentorsLazyQuery();

  const [mentorId, setMentorId] = useState<number | null>(null);
  const [isMentorFetched, setIsMentorFetched] = useState(false);

  const { isOrganizer } = useUserContext();

  const startDate = new Date(mentorShift?.start_time ?? 0);
  const endDate = new Date(mentorShift?.end_time ?? 0);
  const startDateStr = formatVolunteerShiftDay(startDate);
  const endDateStr = formatVolunteerShiftDay(endDate);
  const startTimeStr = getVolunteerShiftHourAndMinute(startDate);
  const endTimeStr = getVolunteerShiftHourAndMinute(endDate);
  const isSameDay = startDateStr === endDateStr;
  const [searchQuery, setSearchQuery] = useState("");

  useEffect(() => {
    if (!searchQuery) {
      getMentorsList({
        variables: {
          where: {},
        },
      });
    }
  }, [getMentorsList, searchQuery]);

  const debouncedSearchUsers = debounce((value: string) => {
    getMentorsList({
      variables: {
        where: {
          user: {
            is: {
              OR: [
                { email: { contains: value, mode: QueryMode.Insensitive } },
                { name: { contains: value, mode: QueryMode.Insensitive } },
              ],
            },
          },
        },
      },
    });
  }, 500);

  const userOptions = useMemo(
    () =>
      mentorsListData?.eventAttendees.slice(0, 20).map((v) => ({
        label: `${v.user.name} (${v.user.email})`,
        value: v.user.id,
      })),
    [mentorsListData]
  );

  const handleAddMentor = useCallback(async () => {
    if (!mentorId) return;
    getMentor({
      variables: {
        where: {
          user: { is: { id: { equals: mentorId } } },
        },
      },
    });
    setIsMentorFetched(true);
  }, [getMentor, mentorId]);

  useEffect(() => {
    if (isMentorFetched && mentorData && mentorData.eventAttendees.length) {
      (async () => {
        try {
          await createShift({
            variables: {
              data: {
                event_attendee_id: mentorData.eventAttendees[0].id,
                shift_id: shiftId,
                checked_in: false,
              },
            },
          });
          onChange?.();
        } catch (e) {
          console.error(e);
        } finally {
          setIsMentorFetched(false);
          setMentorId(null);
        }
      })();
    }
  }, [
    isMentorFetched,
    mentorData,
    createShift,
    shiftId,
    refetchMentorSignups,
    onChange,
  ]);

  const handleDeleteMentor = useCallback(
    async (shiftId: number, eventAttendeeId: number) => {
      try {
        await deleteShiftSignup({
          variables: { shift_id: shiftId, event_attendee_id: eventAttendeeId },
        });
        await refetchMentorSignups();
        onChange?.();
      } catch (e) {
        console.error(e);
      }
    },
    [deleteShiftSignup, refetchMentorSignups, onChange]
  );

  const handleCloseModal = useCallback(() => {
    setOpen(false);
    setMentorId(null);
  }, [setOpen]);

  const columns = useMemo(
    () => [
      { id: "name", name: "Name" },
      { id: "actions", name: "actions", hideLabel: true },
    ],
    []
  );

  const rows = useMemo(() => {
    if (!shiftSignupsData) return [];

    return [...(shiftSignupsData.detailedMentorShifts[0].shift_signups ?? [])]
      .sort((a, b) =>
        a.event_attendee.user.name.localeCompare(b.event_attendee.user.name)
      )
      .map((signup) => ({
        name: signup.event_attendee.user.name,
        actions: (
          <div tw="flex flex-row w-full justify-end">
            <Button
              color="secondary"
              onClick={() =>
                handleDeleteMentor(shiftId, signup.event_attendee.id)
              }
              tw="font-bold"
            >
              <Text tw="text-text-danger">Unregister</Text>
            </Button>
          </div>
        ),
      }));
  }, [shiftSignupsData, handleDeleteMentor, shiftId]);

  if (!mentorShiftData || error || loading || !mentorShift) {
    return null;
  }

  return (
    <StyledModal
      height="auto"
      width="781px"
      scroll
      isOpen={open}
      onClose={handleCloseModal}
    >
      <Text type="h3">{mentorShift.name}</Text>
      <Spacer height={24} />
      <InformationContainer>
        <InformationContainerRow>
          <div>
            <InformationText>
              {isSameDay ? "Date" : "Start Date"}
            </InformationText>
            <Spacer height={8} />
            <Text type="body1">{startDateStr}</Text>
          </div>

          <div>
            <InformationText>Time Start</InformationText>
            <Spacer height={8} />
            <Text type="body1">{startTimeStr}</Text>
          </div>

          {!isSameDay && (
            <div>
              <InformationText>End Date</InformationText>
              <Spacer height={8} />
              <Text type="body1">{endDateStr}</Text>
            </div>
          )}
          <div>
            <InformationText>Time End</InformationText>
            <Spacer height={8} />
            <Text type="body1">{endTimeStr}</Text>
          </div>

          {isSameDay && (
            <div>
              <InformationText>Location</InformationText>
              <Spacer height={8} />
              <Text type="body1">{mentorShift.location}</Text>
            </div>
          )}
        </InformationContainerRow>

        <InformationContainerRow>
          {!isSameDay && (
            <div>
              <InformationText>Location</InformationText>
              <Spacer height={8} />
              <Text type="body1">{mentorShift.location}</Text>
            </div>
          )}

          <div>
            <InformationText>Required Mentors</InformationText>
            <Spacer height={8} />
            <Text type="body1">
              {parseMaxCountRestriction(mentorShift.restrictions)}
            </Text>
          </div>
        </InformationContainerRow>
      </InformationContainer>
      <Spacer height={40} />
      {isOrganizer && (
        <MentorsContainer>
          <InformationText>Mentors</InformationText>

          <StyledTable columns={columns} data={rows} showFooter />

          <AssignMentorContainer>
            <div tw="flex justify-between max-w-[480px] w-full bg-red-50">
              <Autocomplete
                options={userOptions ?? []}
                handleSelect={(value) => setMentorId(Number(value))}
                handleTextInputChange={(value) => {
                  setSearchQuery(value);
                  debouncedSearchUsers(value);
                }}
                renderInput={(props) => (
                  <TextInput
                    type="body1"
                    tw="text-text-tertiary font-medium w-full h-48"
                    onChange={props.onChange}
                    placeholder="Enter mentor email or name"
                    value={props.value}
                  />
                )}
              />
            </div>

            <div tw="w-full h-full! flex justify-end items-center">
              <Button
                size="lg"
                color="primary"
                tw="w-full h-full font-bold"
                onClick={handleAddMentor}
                disabled={mentorId === null}
              >
                Assign Mentor
              </Button>
            </div>
          </AssignMentorContainer>
        </MentorsContainer>
      )}
      <Spacer height={24} />
      {isOrganizer && (
        <div tw="flex flex-row justify-end">
          <Button
            color="secondary"
            onClick={() => {
              navigate("/mentor/shifts/edit?shift_id=" + shiftId);
            }}
          >
            <Text>{"Edit Shift"}</Text>
          </Button>
        </div>
      )}
    </StyledModal>
  );
};

const InformationContainerRow = styled.div`
  display: flex;
  gap: 64px;

  @media (max-width: ${deviceBreakpoints.tablet}px) {
    flex-direction: column;
    gap: 24px;
  }
`;

const InformationContainer = styled.section`
  display: flex;
  flex-direction: column;
  gap: 24px;

  @media (max-width: ${deviceBreakpoints.tablet}px) {
    flex-direction: row;
    gap: 64px;
  }
`;

const MentorsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const AssignMentorContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;

  @media (max-width: ${deviceBreakpoints.tablet}px) {
    flex-direction: column;
    align-items: flex-end;
  }
`;

const InformationText = styled.p`
  font-weight: bold;
  font-size: 20px;
  color: #0e3460;
`;

const StyledTable = styled(Table)`
  overflow: visible;
`;

const StyledModal = styled(Modal)`
  overflow-y: auto;
  max-height: 90vh;
  padding-bottom: 24px;
`;

export default MentorShiftModal;
